import type { InspirationDay } from "@/library/model/wire.js";
import { IconExternalLink } from "@/platform/ui";
import { Modal } from "@/platform/ui";
import { useSetArtworkReaction, useSetQuoteReaction } from "@/library/ui/queries/inspiration.js";
import { fmtDateLong } from "@/platform/client";
import { ArtworkImage } from "@/library/ui/components/ArtworkImage.js";
import { ReactionButtons } from "@/library/ui/components/ReactionButtons.js";

const PROVIDER_LABEL: Record<string, string> = {
  met: "The Met",
  aic: "the Art Institute of Chicago",
  user: "the collection",
};

/** A person as a wall label gives them, `Name (dates)`, skipping what the
 *  placard has nothing to say about. */
function person(name: string | null | undefined, dates: string | null | undefined): string {
  if (!name) return dates ?? "";
  return dates ? `${name} (${dates})` : name;
}

/**
 * Where the art breathes.
 *
 * The composition is an image and a line of text, and **nothing else touches
 * it**. No caption, no author, no title on the face — art explained is not art,
 * and a work that needs a label to land hasn't landed.
 *
 * Everything that explains the piece lives together behind "About this piece",
 * laid out as a museum wall label: two stanzas and a note. There are
 * deliberately **no field names** ("THE IMAGE", "THE QUOTE"). A wall label
 * doesn't caption its own captions, and the earlier version's four type sizes
 * and two-column grid made a dozen short facts read as a form to fill in. The
 * stanzas tell themselves apart by shape: the image leads with a work and
 * carries the museum link, the quote leads with a person.
 *
 * Symmetric on purpose. Captioning the quote but not the painting (or the
 * reverse) is what made the first version read as debris — which is also why
 * a quote carries `source`/`yearDisplay` mirroring an artwork's
 * `title`/`yearDisplay`, so both stanzas can be built the same way.
 */
export function InspirationModal({
  day,
  onClose,
}: {
  day: InspirationDay | null;
  onClose: () => void;
}) {
  const setQuoteReaction = useSetQuoteReaction();
  const setArtworkReaction = useSetArtworkReaction();

  const piece = day?.piece;
  const quote = piece?.quote;
  const artwork = piece?.artwork;
  const museum = artwork ? (PROVIDER_LABEL[artwork.provider] ?? "the museum") : null;

  return (
    <Modal
      open={!!day}
      onClose={onClose}
      size="2xl"
      title={day ? fmtDateLong(new Date(`${day.date}T00:00:00`)) : ""}
    >
      {piece && quote && artwork && (
        <div className="flex flex-col gap-6">
          <ArtworkImage artwork={artwork} fit="natural" eager />

          <blockquote className="mx-auto max-w-2xl text-center font-serif text-xl leading-relaxed text-ink">
            “{quote.text}”
          </blockquote>

          <details className="group border-t border-line pt-3">
            <summary className="flex cursor-pointer list-none items-center gap-1.5 text-[11px] font-bold text-faint transition-colors hover:text-ink-soft">
              <span className="inline-block transition-transform group-open:rotate-90">›</span>
              About this piece
            </summary>

            <div className="mt-5 flex max-w-lg flex-col gap-5 pl-3.5">
              <div className="font-serif text-[17px] leading-snug text-ink">{piece.title}</div>

              {/* The image. Work, hand, dates — a wall label's order.

                  One rule across both stanzas: **a work's title is italic, a
                  person's name is not.** That is the museum convention, and here
                  it also carries the meaning — see the quote's source below. */}
              <div className="flex flex-col gap-0.5">
                <div className="text-[13px] font-semibold text-ink-soft">
                  <span className="italic">{artwork.title}</span>
                  {artwork.yearDisplay && `, ${artwork.yearDisplay}`}
                </div>
                <div className="text-[12px] text-faint">
                  {person(artwork.artist, artwork.artistDates)}
                </div>
                {artwork.sourceUrl && (
                  <a
                    href={artwork.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 inline-flex w-fit items-center gap-1 text-[12px] font-semibold text-accent hover:underline"
                  >
                    View at {museum} <IconExternalLink size={10} />
                  </a>
                )}
              </div>

              {/* The quote. Same shape as the image above — the line itself is
                  already on the face, so it isn't repeated here. A source or year
                  the curator couldn't establish simply goes unsaid; never a hedge
                  (see docs/inspiration.md).

                  The source is *italic* because it is a work's title, which is
                  also what disambiguates it: a work can be named after a person
                  (Hugo's *William Shakespeare*, 1864), and without the italic
                  "Victor Hugo (1802–1885)" above "William Shakespeare, 1864"
                  reads as two people rather than an author and their book. */}
              <div className="flex flex-col gap-0.5">
                <div className="text-[13px] font-semibold text-ink-soft">
                  {person(quote.author, quote.authorDates)}
                </div>
                {(quote.source || quote.yearDisplay) && (
                  <div className="text-[12px] text-faint">
                    {quote.source && <span className="italic">{quote.source}</span>}
                    {quote.source && quote.yearDisplay && ", "}
                    {quote.yearDisplay}
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-1.5 border-t border-line/60 pt-4">
                <div className="text-[11px] font-bold text-muted">A note from the curator</div>
                <p className="font-serif text-[13.5px] leading-relaxed text-ink-soft">
                  {piece.note}
                </p>
              </div>
            </div>
          </details>

          {/* Two separate controls, because they are two separate opinions —
              loving a line of Rilke says nothing about the painting it arrived
              with. Labelled here (unlike the tiles) because there is room to. */}
          <div className="flex items-center justify-end gap-4 border-t border-line pt-4">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wide text-faint">
                Quote
              </span>
              <ReactionButtons
                label="quote"
                reaction={quote.reaction}
                onChange={(reaction) => setQuoteReaction.mutate({ id: quote.id, reaction })}
                size={15}
              />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wide text-faint">
                Image
              </span>
              <ReactionButtons
                label="image"
                reaction={artwork.reaction}
                onChange={(reaction) => setArtworkReaction.mutate({ id: artwork.id, reaction })}
                size={15}
              />
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
